// Cluster ZUWEISEN (C1/C3/C5/C8) — die Anlage aus dem Zuweisen-Popup als reine
// Orchestrierung. Alle I/O-Wege kommen als Deps herein (Vorbild:
// relationsInline/persistRelations.ts) → in Vitest ohne Netzwerk prüfbar.
//
// Warum KEIN transaktionaler Sammel-Endpoint (Annahme A7): er dupliziert
// `contact.createWithTags` UND `ansprechpartner-cleanup.saveBatch` inklusive
// FO/BO-Vererbung, Statuslogik und Beziehungs-Guards. Stattdessen merkt sich
// `AnlageStand`, was ein vorheriger Versuch schon geschafft hat — ein
// Wiederholversuch legt nichts doppelt an.

import {
    buildApMenschItems,
    istInterneAdresse,
    buildFirmaPayload,
    buildPrivatPayload,
    resolveCreatedApId,
    type ApWerte,
    type AssignOwnership,
    type FirmaWerte,
    type KontaktRef,
    type MailZuweisungErgebnis,
    type StatusWahl,
    type PrivatWerte,
} from "./mailContactAssign";
import { buildMenschenItems, type MenschItem } from "../verantwortlichkeit/buildMenschenItems";
import type { ContactRef } from "../relationsInline/types";
import type { Ownership } from "../relationsInline/persistRelations";
import type { CreatedPerson } from "../../services/menschenFuersUnternehmenService";
import type { VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import { relationBodies } from "../relationsInline/persistRelations";
import { hatVerbindungsAngabe, istMenschImUnternehmen } from "./mailVerantwortlichkeit";

/**
 * Verbindung eines Privatkontakts zu einer Firma (C3). Seit 01.10.2026 die
 * volle Verantwortlichkeit wie in der Vollanlage („Verbindung zu
 * Unternehmen") statt der Wahl „Mensch fürs Unternehmen | Mitarbeiter".
 */
export type FirmaVerbindung = {
    firmaId: number;
    firmaName: string;
    verant: VerantwortlichkeitValue;
};

export type AnlageEingabe =
    | {
          /** Neue Firma (C5) — eine bestehende wird hier nie gewählt. */
          typ: "firma";
          status: StatusWahl | string;
          firma: FirmaWerte;
          /** Ansprechpartner-Block; `null` = aus. */
          ap: ApWerte | null;
          ownership: AssignOwnership;
      }
    | {
          /**
           * Neue Person als Ansprechpartner einer BESTEHENDEN Firma (früher
           * „Firma → bestehende wählen", seit dem Wegfall des Umschalters im
           * Personen-Zweig). Status, FO/BO und Firmenbezug kommen serverseitig
           * über attach-menschen — darum kein Status und keine Ownership hier.
           */
          typ: "ap";
          firma: KontaktRef;
          ap: ApWerte;
      }
    | {
          typ: "privat";
          status: StatusWahl | string;
          person: PrivatWerte;
          firmaVerbindung: FirmaVerbindung | null;
          ownership: AssignOwnership;
      };

/** Was ein vorheriger Versuch bereits erreicht hat — der Wiederholversuch überspringt es. */
export type AnlageStand = {
    firmaId?: number;
    firmaName?: string;
    personId?: number;
    personName?: string;
    relationOk?: boolean;
    /** Arbeitgeber („Mensch im Unternehmen") beim Privatkontakt gesetzt. */
    arbeitgeberOk?: boolean;
    /**
     * Die Person ist über die Vollanlage entstanden („+ Weitere Informationen
     * erfassen") und muss noch als Ansprechpartner an die bestehende Firma —
     * attach-menschen mit ihrer Id statt einer neuen Person.
     */
    apVerbindenOffen?: boolean;
};

export type AnlageDeps = {
    /** POST /contact → id des neuen Kontakts. */
    postContact: (body: Record<string, unknown>) => Promise<number | null>;
    /** POST /ansprechpartner-cleanup/attach-menschen. */
    attachMenschen: (items: MenschItem[]) => Promise<CreatedPerson[]>;
    /** POST /contact-relation (Firma → Person). */
    postRelation: (body: Record<string, unknown>) => Promise<unknown>;
    /**
     * PUT /contact/:id/arbeitgeber — „Mensch im Unternehmen" beim
     * Privatkontakt. Derselbe Weg wie in der Vollanlage: Dort belegt der Haken
     * den Arbeitgeber vor, und erst der schreibt die Verbindung „Mensch im
     * Unternehmen" (POST contact-relation kennt den Haken nicht).
     */
    setzeArbeitgeber?: (personId: number, firmaId: number) => Promise<unknown>;
    /** Contact-Typ-ids (Firma/Person) — dürfen null sein, der Payload hat Fallbacks. */
    typeIds: { firma: number | null; person: number | null };
    /**
     * T-43/B6: eigene Domänen des Mandanten. Eine interne E-Mail-Adresse
     * (Kollege) wird an keinem neu angelegten Kontakt gespeichert — auch nicht,
     * wenn sie von Hand eingetragen wurde.
     */
    interneDomaenen?: ReadonlySet<string>;
};

export type AnlageErgebnis =
    | { stand: AnlageStand; ergebnis: MailZuweisungErgebnis }
    | { stand: AnlageStand; fehler: string };

/** Menschenlesbare Fehlermeldung aus einem beliebigen Wurf. */
function meldung(e: unknown, fallback: string): string {
    const m = (e as { message?: unknown })?.message;
    return typeof m === "string" && m.trim() ? m : fallback;
}

const personName = (lastName: string, firstName?: string): string => {
    const l = lastName.trim();
    const f = (firstName ?? "").trim();
    return f ? `${l}, ${f}` : l;
};

/**
 * Legt den Kontakt an — Firma (+ optional Ansprechpartner), Ansprechpartner
 * einer bestehenden Firma oder Privatkontakt (+ optional Firmenverbindung).
 *
 * Schlägt ein SPÄTERER Schritt fehl, ist das Vorherige trotzdem da: die
 * Funktion liefert dann `fehler` UND den aktualisierten `stand`. Das Fenster
 * kann damit „Erneut versuchen" (überspringt Erledigtes) und — bei der reinen
 * Firmenverbindung — „Trotzdem zuweisen" anbieten.
 */
export async function legeKontaktAn(
    eingabe: AnlageEingabe,
    stand: AnlageStand,
    deps: AnlageDeps,
): Promise<AnlageErgebnis> {
    const s: AnlageStand = { ...stand };

    if (eingabe.typ === "firma" || eingabe.typ === "ap") {
        // (1) Firma — die gewählte übernehmen oder neu anlegen.
        if (eingabe.typ === "ap") {
            s.firmaId = eingabe.firma.id;
            s.firmaName = eingabe.firma.displayName;
        } else if (!s.firmaId) {
            try {
                const id = await deps.postContact(
                    buildFirmaPayload(
                        eingabe.firma,
                        eingabe.status,
                        eingabe.ownership,
                        deps.typeIds.firma,
                        deps.interneDomaenen,
                    ),
                );
                if (!id) throw new Error("Die Antwort enthielt keine Kontakt-Id.");
                s.firmaId = id;
                s.firmaName = eingabe.firma.name.trim();
            } catch (e) {
                return {
                    stand: s,
                    fehler: `Die Firma konnte nicht angelegt werden: ${meldung(e, "unbekannter Fehler")}`,
                };
            }
        }

        // (2) Ansprechpartner unter der Firma (C5).
        if (eingabe.ap && s.personId && s.apVerbindenOffen) {
            // Vollanlage: Die Person steht schon — nur noch an die Firma hängen.
            try {
                const ref: ContactRef = { contactId: s.personId, type: "Person" };
                const mitRollen = buildMenschenItems([{ ref, verant: eingabe.ap.verant ?? null }], s.firmaId!);
                await deps.attachMenschen(
                    mitRollen.length
                        ? mitRollen
                        : [
                              {
                                  relationId: 0,
                                  personId: 0,
                                  companyId: s.firmaId!,
                                  isMitarbeiterBav: false,
                                  isNew: true,
                                  role: "mensch",
                                  verantwortlichkeit: "",
                                  newPerson: { contactId: s.personId },
                              },
                          ],
                );
                s.apVerbindenOffen = false;
            } catch (e) {
                return {
                    stand: s,
                    fehler:
                        `${s.personName ?? "Der Ansprechpartner"} ist angelegt, die Verbindung zu ${s.firmaName ?? "der Firma"} nicht: ` +
                        `${meldung(e, "unbekannter Fehler")}. „Erneut versuchen" legt den Kontakt nicht doppelt an.`,
                };
            }
        }

        if (eingabe.ap && !s.personId) {
            try {
                const created = await deps.attachMenschen(
                    buildApMenschItems({
                        firmaId: s.firmaId!,
                        lastName: eingabe.ap.lastName,
                        firstName: eingabe.ap.firstName,
                        email: eingabe.ap.email,
                        telefon: eingabe.ap.telefon,
                        mobil: eingabe.ap.mobil,
                        verant: eingabe.ap.verant,
                        interneDomaenen: deps.interneDomaenen,
                    }),
                );
                const id = resolveCreatedApId(created);
                if (!id) throw new Error("Die Antwort enthielt keinen neuen Ansprechpartner.");
                s.personId = id;
                s.personName = personName(eingabe.ap.lastName, eingabe.ap.firstName);
            } catch (e) {
                return {
                    stand: s,
                    fehler:
                        eingabe.typ === "ap"
                            ? `Der Ansprechpartner konnte nicht angelegt werden: ${meldung(e, "unbekannter Fehler")}`
                            : `Die Firma ${s.firmaName ?? ""} steht, der Ansprechpartner nicht: ` +
                              `${meldung(e, "unbekannter Fehler")}. „Erneut versuchen" legt die Firma nicht doppelt an.`,
                };
            }
        }

        const firma: KontaktRef = { id: s.firmaId!, displayName: s.firmaName ?? "Firma" };
        const kontakt: KontaktRef = s.personId
            ? { id: s.personId, displayName: s.personName ?? "Ansprechpartner" }
            : firma;
        const angelegt: MailZuweisungErgebnis["angelegt"] = [];
        if (eingabe.typ === "firma") angelegt.push("firma");
        if (s.personId) angelegt.push("ap");
        return {
            stand: s,
            ergebnis: {
                kontakt,
                // Der Vorgang läuft auf die Firma — aber nur, wenn der
                // Adressträger ein ANDERER ist (sonst wäre firma = kontakt).
                firma: s.personId ? firma : null,
                weiter: "zuweisen",
                adresseUebernommen: false,
                angelegt,
            },
        };
    }

    // ── Privat ───────────────────────────────────────────────────────────────
    if (!s.personId) {
        try {
            const id = await deps.postContact(
                buildPrivatPayload(
                    eingabe.person,
                    eingabe.status,
                    eingabe.ownership,
                    deps.typeIds.person,
                    eingabe.firmaVerbindung?.firmaId ?? null,
                    deps.interneDomaenen,
                ),
            );
            if (!id) throw new Error("Die Antwort enthielt keine Kontakt-Id.");
            s.personId = id;
            s.personName = personName(eingabe.person.lastName, eingabe.person.firstName);
        } catch (e) {
            return {
                stand: s,
                fehler: `Der Kontakt konnte nicht angelegt werden: ${meldung(e, "unbekannter Fehler")}`,
            };
        }
    }

    const kontakt: KontaktRef = { id: s.personId, displayName: s.personName ?? "Kontakt" };

    // (2) Verbindung zur Firma (C3) — wie die Vollanlage („Verbindung zu
    // Unternehmen", persistRelations): je Verantwortlichkeit eine Zeile,
    // Richtung src = Firma. Der Haken „Mensch im Unternehmen" läuft wie dort
    // über den Arbeitgeber-Weg.
    const fv = eingabe.firmaVerbindung;
    if (fv && hatVerbindungsAngabe(fv.verant) && !(s.relationOk && s.arbeitgeberOk)) {
        try {
            if (!s.relationOk) {
                for (const body of relationBodies("personFirma", { verant: fv.verant })) {
                    await deps.postRelation({
                        contactSrcId: fv.firmaId,
                        contactDestId: s.personId,
                        ...body,
                    });
                }
                s.relationOk = true;
            }
            if (istMenschImUnternehmen(fv.verant) && deps.setzeArbeitgeber) {
                await deps.setzeArbeitgeber(s.personId!, fv.firmaId);
            }
            s.arbeitgeberOk = true;
        } catch (e) {
            return {
                stand: s,
                fehler:
                    `Der Kontakt wurde angelegt, die Verbindung zu ${fv.firmaName} nicht: ` +
                    `${meldung(e, "unbekannter Fehler")}. Du kannst sie später im Kontakt ergänzen.`,
            };
        }
    }

    return {
        stand: s,
        ergebnis: {
            kontakt,
            // ABWEICHUNG vom Plan, bewusst (E14): `firma` bleibt hier NULL, auch
            // wenn eine Firmenverbindung angelegt wurde. `firma` bedeutet in
            // diesem Vertrag „die Firma, deren ANSPRECHPARTNER der Kontakt ist"
            // — daraus leitet `vorgangKontaktAus` den Vorgangs-Kunden ab. Ein
            // Privatkontakt behält aber seinen Hauptstatus (Interessent/Kunde);
            // „Verantwortung fürs Unternehmen" kommt nur als Zusatzstatus dazu. Der
            // Vorgang gehört also der PERSON, nicht der verbundenen Firma.
            // Umkehrbar an genau dieser Zeile.
            firma: null,
            weiter: "zuweisen",
            adresseUebernommen: false,
            angelegt: ["privat"],
        },
    };
}

// ── Vollanlage aus der Schnellanlage (User 02.10.2026) ─────────────────────
//
// „+ Weitere Informationen erfassen" öffnet die volle Kontaktanlage mit dem,
// was in der Schnellanlage steht. Sie legt den HAUPTkontakt an (die neue Firma
// bzw. die Person); alles danach — Ansprechpartner der neuen Firma, Verbindung
// der Person zur Firma, Ansprechpartner einer bestehenden Firma — läuft wie in
// der Schnellanlage über `legeKontaktAn`, mit dem neuen Kontakt im `stand`.
// So entsteht nichts doppelt, und das Ergebnis ist dasselbe, „als wäre er in
// der Schnellanlage angelegt worden".

export type MailVollanlage = {
    /** Stand der Schnellanlage als ContactRef (für `refZuVorbelegung`). */
    kontakt: ContactRef;
    /** Status-Felder der Schnellanlage (bzw. der feste Status des Ansprechpartners). */
    zusatzFelder: Record<string, string>;
    /** FO/BO der Schnellanlage bzw. — beim Ansprechpartner — die Firma, von der er erbt. */
    eltern: { elternId?: number; elternName?: string; ownership?: Ownership | null };
};

const t = (v?: string | null) => (v ?? "").trim();

/** Schnellanlage der Mail → Vorbelegung der Vollanlage. */
export function mailVollanlageVorbereiten(
    eingabe: AnlageEingabe,
    statusFelder: Record<string, string>,
    interneDomaenen?: ReadonlySet<string>,
): MailVollanlage {
    const mail = (v?: string) => (t(v) && !istInterneAdresse(t(v), interneDomaenen) ? t(v) : undefined);
    const status: Record<string, string> = {};
    for (const k of ["kontaktStatus", "kontaktStatusRollen", "kontaktStatusAuch"]) {
        if (t(statusFelder[k])) status[k] = t(statusFelder[k]);
    }
    const ownership = (o: AssignOwnership): Ownership => ({
        ...(t(o.foUserId) ? { foUserId: t(o.foUserId) } : {}),
        ...(t(o.boType) ? { boType: t(o.boType) } : {}),
        ...(t(o.boUserId) ? { boUserId: t(o.boUserId) } : {}),
        ...(t(o.boTeamId) ? { boTeamId: t(o.boTeamId) } : {}),
    });

    if (eingabe.typ === "firma") {
        const f = eingabe.firma;
        const lf = Number(f.legalFormId);
        return {
            kontakt: {
                type: "Firma",
                manual: true,
                lastName: t(f.name),
                ...(t(f.gesellschaftsform) ? { legalForm: t(f.gesellschaftsform) } : {}),
                ...(lf > 0 ? { legalFormId: lf } : {}),
                email: mail(f.email),
                phone: t(f.telefon) || undefined,
                mobile: t(f.mobil) || undefined,
                street: t(f.strasse) || undefined,
                postalCode: t(f.plz) || undefined,
                city: t(f.ort) || undefined,
            },
            zusatzFelder: status,
            eltern: { ownership: ownership(eingabe.ownership) },
        };
    }

    if (eingabe.typ === "ap") {
        const ap = eingabe.ap;
        return {
            kontakt: {
                type: "Person",
                manual: true,
                lastName: t(ap.lastName),
                firstName: t(ap.firstName) || undefined,
                emailWork: mail(ap.email),
                phoneWork: t(ap.telefon) || undefined,
                mobileWork: t(ap.mobil) || undefined,
            },
            // Wie die Schnellanlage: Der Ansprechpartner bekommt „Verantwortung
            // fürs Unternehmen" und erbt FO/BO der Firma (über ihre Id).
            zusatzFelder: { kontaktStatus: "Sonstige", kontaktStatusRollen: "Verantwortung fürs Unternehmen" },
            eltern: { elternId: eingabe.firma.id, elternName: eingabe.firma.displayName },
        };
    }

    const p = eingabe.person;
    const kanal = (art: KommArtLokal, privat: keyof ContactRef, arbeit: keyof ContactRef, wert?: string) =>
        wert ? { [art === "geschaeftlich" ? arbeit : privat]: wert } : {};
    return {
        kontakt: {
            type: "Person",
            manual: true,
            lastName: t(p.lastName),
            firstName: t(p.firstName) || undefined,
            ...kanal(p.emailArt, "emailPrivate", "emailWork", mail(p.email)),
            ...kanal(p.telefonArt, "phonePrivate", "phoneWork", t(p.telefon) || undefined),
            ...kanal(p.mobilArt, "mobilePrivate", "mobileWork", t(p.mobil) || undefined),
        } as ContactRef,
        zusatzFelder: status,
        eltern: { ownership: ownership(eingabe.ownership) },
    };
}

type KommArtLokal = "privat" | "geschaeftlich";

/**
 * Der `stand` nach der Vollanlage: der dort angelegte Hauptkontakt gilt als
 * erledigt, `legeKontaktAn` macht mit dem Rest weiter.
 */
export function standNachVollanlage(
    eingabe: AnlageEingabe,
    stand: AnlageStand,
    contactId: number,
    name: string,
): AnlageStand {
    if (eingabe.typ === "firma") return { ...stand, firmaId: contactId, firmaName: name };
    if (eingabe.typ === "ap") {
        return {
            ...stand,
            firmaId: eingabe.firma.id,
            firmaName: eingabe.firma.displayName,
            personId: contactId,
            personName: name,
            apVerbindenOffen: true,
        };
    }
    return { ...stand, personId: contactId, personName: name };
}
